import { fabric } from 'fabric';
import { SequenceInfo } from '../Components/AnnotationManager/Annotation/annotation.types';
import { SequenceImage } from '../Components/StitchingTool/Stitching/Stitching.reducer';

export type StitchingManipulationRules = {
  lockMovementX: boolean;
  lockMovementY: boolean;
  lockScalingX: boolean;
  lockScalingY: boolean;
  lockRotation: boolean;
};

export type FabricObjectWithSrc = Required<
  Pick<
    fabric.IObjectOptions,
    | 'top'
    | 'left'
    | 'angle'
    | 'name'
    | 'scaleX'
    | 'scaleY'
    | 'width'
    | 'height'
    | 'originX'
    | 'originY'
    | 'centeredRotation'
    | 'lockUniScaling'
    | 'selectable'
    | 'hasControls'
    | 'hasBorders'
    | 'originY'
    | 'data'
    | 'visible'
    | keyof StitchingManipulationRules
  > & {
    src: string;
    stackingRank: number;
  }
>;

type MapToFabricImageArgs = {
  rawImages: any[];
  isStitched: boolean;
  canvasHeight: number;
  canvasWidth: number;
  subsampleFactor: number;
};

type MapToFabricImageResult = {
  fabricImages: FabricObjectWithSrc[];
  topScale: number;
  leftScale: number;
  centringLeftScale: number;
  zoom: number;
};

export const TABLE_STATUS = {
  HIDE: 0,
  NORMAL: 1,
  EXPANDED: 2,
  goUp: (currentTableStatus: number) => currentTableStatus + 1,
  goDown: (currentTableStatus: number) => currentTableStatus - 1
};

export type ChangableFabricProps = ReturnType<typeof extractFabricObjectMovementProperties>;

const mapAndScaleImages = (images: any[], leftScale: number, topScale: number, subsampleFactor: number) => {
  const ratio = 1 / subsampleFactor;
  return images.map(image => {
    return {
      left: (image.left - leftScale) * ratio,
      top: (image.top - topScale) * ratio,
      width: image.width * ratio,
      height: image.height * ratio,
      angle: image.rotation * -1,
      src: image.src,
      scaleX: image.scale,
      scaleY: image.scale,
      name: image.name,
      visible: !image.isHidden,
      stitchingManipulationRule: image.stitchingManipulationRule,
      isHomographyValid: image.isHomographyValid,
      stackingRank: image.stackingRank
    };
  });
};

function extractFabricObjectMovementProperties(fabricObj: fabric.IObjectOptions & { isHomographyValid: boolean }) {
  return {
    left: fabricObj.left!,
    top: fabricObj.top!,
    angle: fabricObj.angle!,
    name: fabricObj.name!,
    scaleX: fabricObj.scaleX!,
    scaleY: fabricObj.scaleY!,
    width: fabricObj.width!,
    height: fabricObj.height!,
    visible: fabricObj.visible!,
    lockMovementX: fabricObj.lockMovementX!,
    lockMovementY: fabricObj.lockMovementY!,
    lockRotation: fabricObj.lockRotation!,
    lockScalingX: fabricObj.lockScalingX!,
    lockScalingY: fabricObj.lockScalingY!,
    isHomographyValid: (fabricObj as any).isHomographyValid!,
    data: fabricObj.data
  };
}

const calculateZoom = (images: any[], canvasHeight: number) => {
  if (images.length === 0) return 1;

  const maxTopPostion = Math.max(...images.map(i => i.top)) + images[images.length - 1].height; // + 50;

  const zoom = maxTopPostion / canvasHeight;

  return zoom;
};

const mapRawToFabricImages = ({
  rawImages,
  isStitched,
  canvasHeight,
  canvasWidth,
  subsampleFactor
}: MapToFabricImageArgs): MapToFabricImageResult => {
  if (rawImages.length === 0) {
    return {
      fabricImages: [],
      topScale: 1,
      leftScale: 1,
      centringLeftScale: 1,
      zoom: 1
    };
  }

  const newImages = rawImages.filter(x => !x.isHidden);

  const topScale = Math.min(...newImages.map(i => i.top));
  const leftScale = Math.min(...newImages.map(i => i.left));

  const images = mapAndScaleImages(newImages, leftScale, topScale, subsampleFactor);
  const zoom = calculateZoom(images, canvasHeight);

  const centringLeftScale = (canvasWidth * zoom) / 2;

  const fabricImages = images.map(imageObj => {
    return {
      left: imageObj.left + centringLeftScale,
      src: imageObj.src,
      top: imageObj.top,
      angle: imageObj.angle,
      name: imageObj.name,
      scaleX: imageObj.scaleX,
      scaleY: imageObj.scaleY,
      width: imageObj.width,
      height: imageObj.height,
      originX: 'left',
      originY: 'top',
      centeredRotation: true,
      lockUniScaling: true,
      lockScalingFlip: true,
      selectable: !isStitched,
      hasControls: !isStitched,
      hasBorders: !isStitched,
      data: {
        name: imageObj.name,
        stackingRank: imageObj.stackingRank
      },
      visible: true,
      ...imageObj.stitchingManipulationRule,
      isHomographyValid: imageObj.isHomographyValid
    };
  });

  return { fabricImages, topScale, leftScale, centringLeftScale, zoom };
};

const scaleBackImages = (images: any, leftScale: number, centringLeftScale: number, topScale: number, subsampleFactor: number) => {
  const ratio = 1 / subsampleFactor;
  return images.map((image: any) => {
    return {
      left: (image.left! - centringLeftScale) / ratio + leftScale,
      top: image.top! / ratio + topScale,
      width: Math.round(image.width! / ratio),
      height: Math.round(image.height! / ratio),
      rotation: image.angle! * -1,
      scale: image.scaleX,
      name: image.name,
      isHidden: !image.visible,
      stitchingManipulationRule: getStitchingManipulationRules(image),
      isHomographyValid: image.isHomographyValid,
      stackingRank: image.data.stackingRank
    };
  });
};

function mapGroupPositionToObjectPostion(obj: any) {
  const matrix1 = obj.group.calcTransformMatrix();
  const matrix2 = obj.calcTransformMatrix();

  const options = fabric.util.qrDecompose(matrix2);

  const { x: left, y: top } = fabric.util.transformPoint(new fabric.Point(obj.left, obj.top), matrix1);

  return extractFabricObjectMovementProperties({
    name: obj.name,
    left: left,
    top: top,
    width: obj.width,
    height: obj.height,
    visible: obj.visible,
    lockMovementX: obj.lockMovementX,
    lockMovementY: obj.lockMovementY,
    lockRotation: obj.lockRotation,
    lockScalingX: obj.lockScalingX,
    lockScalingY: obj.lockScalingY,
    isHomographyValid: obj.isHomographyValid,
    data: obj.data,
    ...options
  });
}

function shallowCompare(left: Record<string, any>, right: Record<string, any>) {
  const allKeys = Object.keys({ ...left, ...right });

  let equal = true;
  allKeys.forEach(key => {
    if (left[key] !== right[key]) {
      equal = false;
    }
  });

  return equal;
}

function getImageNameWithoutExtensions(imageName: string) {
  return imageName.replace('.jpg', '').replace('.png', '');
}

const getStitchingManipulationRulesFromActiveSelection = (activeObject: any): StitchingManipulationRules => {
  const multipleObjectSelected = activeObject && (activeObject as any)._objects && (activeObject as any)._objects.length > 0;
  const lockMovementX = multipleObjectSelected
    ? (activeObject as any)._objects.some((x: { lockMovementX: any }) => x.lockMovementX)
    : activeObject.lockMovementX;
  const lockMovementY = multipleObjectSelected
    ? (activeObject as any)._objects.some((x: { lockMovementY: any }) => x.lockMovementY)
    : activeObject.lockMovementY;
  const lockRotation = multipleObjectSelected
    ? (activeObject as any)._objects.some((x: { lockRotation: any }) => x.lockRotation)
    : activeObject.lockRotation;
  const lockScalingX = multipleObjectSelected
    ? (activeObject as any)._objects.some((x: { lockScalingX: any }) => x.lockScalingX)
    : activeObject.lockScalingX;
  const lockScalingY = multipleObjectSelected
    ? (activeObject as any)._objects.some((x: { lockScalingY: any }) => x.lockScalingY)
    : activeObject.lockScalingY;

  return {
    lockMovementX,
    lockMovementY,
    lockRotation,
    lockScalingX,
    lockScalingY
  };
};

const getStitchingManipulationRules = (image: any) => {
  return {
    lockMovementX: image.lockMovementX,
    lockMovementY: image.lockMovementY,
    lockRotation: image.lockRotation,
    lockScalingX: image.lockScalingX,
    lockScalingY: image.lockScalingY
  };
};

const sendImagesToBack = (canvasEl: fabric.Canvas, names: string[]) => {
  const objects = canvasEl.getObjects();
  const activeObjects = canvasEl.getActiveObjects();
  const sortedByRanks = objects.sort(sortImagesAscFunction);
  const changedRanks = sortedByRanks.filter(object => names.includes(object.data.name)).map(object => object.data.stackingRank as number);

  let backIndex = sortedByRanks[0].data.stackingRank;
  let changedRanksLeft = changedRanks.length;

  sortedByRanks.forEach((object, index) => {
    if (index < changedRanks[changedRanks.length - 1] + 1) {
      object.data.stackingRank += changedRanksLeft;
    }

    if (names.includes(object.data.name)) {
      object.data.stackingRank = backIndex++;
      changedRanksLeft--;
    }
  });

  const isMultiSelection = activeObjects.length > 1;

  return sortedByRanks.map(object => {
    return isMultiSelection && names.includes(object.data.name)
      ? mapGroupPositionToObjectPostion(object)
      : extractFabricObjectMovementProperties(object as any);
  });
};

const bringImagesToFront = (canvasEl: fabric.Canvas, names: string[]) => {
  const objects = canvasEl.getObjects();
  const activeObjects = canvasEl.getActiveObjects();
  const sortedByRanks = objects.sort(sortImagesDescFunction);
  const changedRanks = sortedByRanks.filter(object => names.includes(object.data.name)).map(object => object.data.stackingRank as number);

  let frontIndex = sortedByRanks[0].data.stackingRank;
  let changedRanksLeft = changedRanks.length;

  sortedByRanks.forEach((object, index) => {
    if (index < sortedByRanks.length - changedRanks[changedRanks.length - 1]) {
      object.data.stackingRank -= changedRanksLeft;
    }

    if (names.includes(object.data.name)) {
      object.data.stackingRank = frontIndex--;
      changedRanksLeft--;
    }
  });

  const isMultiSelection = activeObjects.length > 1;

  return sortedByRanks.map(object => {
    return isMultiSelection && names.includes(object.data.name)
      ? mapGroupPositionToObjectPostion(object)
      : extractFabricObjectMovementProperties(object as any);
  });
};

const populateSequenceImageInfoWithRawData = (sequence: SequenceInfo, images: SequenceImage[]) => {
  sequence.images.forEach(image => {
    const stitchedImage = images.find(x => x.name === image.filename);
    image.isHidden = stitchedImage ? stitchedImage.isHidden : true;
    image.top = stitchedImage ? stitchedImage.top : undefined;
  });
};

const sortImagesAscFunction = (a: any, b: any) => a.data.stackingRank - b.data.stackingRank;

const sortImagesDescFunction = (a: any, b: any) => b.data.stackingRank - a.data.stackingRank;

const sortImagesAscByTopFunction = (a: any, b: any) => a.top - b.top;

const sortImagesDescByTopFunction = (a: any, b: any) => b.top - a.top;

const deleteOrDeprecate = (canDelete: boolean) => (canDelete ? 'Delete' : 'Deprecate');

export {
  mapAndScaleImages,
  scaleBackImages,
  mapRawToFabricImages,
  calculateZoom,
  extractFabricObjectMovementProperties,
  mapGroupPositionToObjectPostion,
  shallowCompare,
  getImageNameWithoutExtensions,
  getStitchingManipulationRulesFromActiveSelection,
  sendImagesToBack,
  bringImagesToFront,
  sortImagesAscFunction,
  sortImagesDescFunction,
  populateSequenceImageInfoWithRawData,
  sortImagesAscByTopFunction,
  sortImagesDescByTopFunction,
  deleteOrDeprecate
};
